import React, { useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from "react-transition-group";

import "./style.sass";

import Layout from '../../components/Layout';
import { userInfo } from '../../contexts/user';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import useFetchWallData from '../../utils/useFetchWallData';
import GreetingByCards from './greeting-by-cards';
import CreateSection from './greeting-create-section';
import GreetingByFBComments from './greeting-by-social';
import Share from '../../components/Share';

import { ClassicBanner, CarouselBanner } from '../../components/Banner';

const GreetingBanner = ({ wall }) => {
    const config = JSON.parse(wall.config)
    switch (wall.layout) {
        case 'classic':
            return <div className="box"><ClassicBanner data={config} readonly={true} heroClassName="is-medium" /></div>
        case 'carousel':
            return <div className="box"><CarouselBanner data={config} readonly={true} heroClassName="is-medium" /></div>
        default:
            return <div>Not implemented</div>
    }
}

const GreetingSection = ({ userId, userName, wall, isGreeted }) => {
    switch (wall.greetingType) {
        case 'cards':
            return <GreetingByCards userId={userId} userName={userName} wall={wall} isGreeted={isGreeted} />
        case 'fb-comments':
            return <div className="box"><GreetingByFBComments /></div>
        default:
            return <div>Not implemented</div>
    }
}

const OrganizeBySection = ({ organizedBy }) => {
    return (
        <section className='py-4 has-background-white'>
            <nav className="level">
                <div className="level-item has-text-centered">
                    <div>
                        <p className="heading">Proudly organized by</p>
                        <p className="title is-4">{organizedBy}</p>
                    </div>
                </div>
            </nav>
        </section>
    )
}

const ShareSection = ({ isOwner, wallId, occasion, isRecipient }) => {
    const link = "https://greetings.wallknown.com/greeting/" + wallId
    const excerpt = "You are invited for a " + occasion + " greeting"
    const title = "Birthday greeting"

    return (
        <section className='pb-5'>
            <div className="has-text-centered">
                <div className='columns'>
                    {!isRecipient && 
                        <div className='column'>
                            <div className="box">
                                <p className="heading py-2">Let others know</p>
                                <Share title={title} url={link} excerpt={excerpt} />
                            </div>
                        </div>
                    }

                    {isOwner &&
                        <div className='column'>
                            <div className="box">
                                <p className="heading py-2">Deliver to recipient</p>
                                <Share
                                    title={title}
                                    url={`${link}?r=${wallId}`}
                                    excerpt="Your greeting wall is awaiting"
                                    showFB={false} showTwitter={false} />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </section>
    )
}

const WallNotFound = () => {
    return (
        <>
            <section className='hero is-primary is-bold has-text-centered is-medium'>
                <div className='hero-body'>
                    <div className='container'>
                        <div className='columns'>
                            <div className='column is-10 is-offset-1'>
                                <div className='section is-centered'>
                                    <p className='title is-size-3 py-2' >
                                        Greeting wall not found
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <CreateSection />
        </>
    )
}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const GreetingPage = ({ user }) => {
    const history = useHistory();
    const { wallId } = useParams();
    const query = useQuery();

    if (!wallId) history.push('/create');

    const [showContent, setShowContent] = useState(false);
    const { userId, userName } = userInfo(user)
    const recipientToken = query.get("r");
    const {
        wall, isGreeted, isLoading, isRecipient
    } = useFetchWallData({ userId, wallId, userName, recipientToken })

    const Content = () => {
        if (showContent) {
            if (wall.id) {
                return (
                    <>
                        <div className='columns is-centered'>
                            <main className="main column">
                                <OrganizeBySection organizedBy={wall.senderName} />
                                <GreetingBanner wall={wall} />
                                <GreetingSection userId={userId} userName={userName} wall={wall} isGreeted={isGreeted} isRecipient={isRecipient} />
                                <ShareSection isOwner={userId === wall.senderId} wallId={wallId} occasion={wall.occasion} isRecipient={isRecipient} />
                                <CreateSection />
                            </main>
                        </div>
                    </>
                )
            } else {
                return <WallNotFound />
            }
        }
        return null
    }

    return (
        <Layout>
            <TransitionGroup component={null}>
                {isLoading && (
                    <CSSTransition classNames="fade" timeout={300} onExited={() => setShowContent(true)}>
                        <section className='hero is-primary is-bold is-large'>
                            <div className='hero-body'>
                                <div className='container'>
                                    <LoadingSpinner
                                        topMessage="Loading"
                                        topMessageClassName="has-text-light title mb-2 is-monospace"
                                        loaderType="Hearts"
                                        quote="'The way you greet someone determines how he will welcome you.'"
                                        quoteClassName='pb-6 is-italic has-text-centered' />
                                </div>
                            </div>
                        </section>
                    </CSSTransition>
                )}
            </TransitionGroup>
            <Content />
        </Layout>
    );
}

export default GreetingPage;